import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Box,
    Grid,
    Card,
    CardMedia,
    CardActions,
    IconButton,
    Typography,
    CircularProgress
} from "@mui/material";
import { AddPhotoAlternate, VideoLibrary, Delete, ZoomIn } from "@mui/icons-material";
import MDButton from "components/MDButton";
import CropModal from "./CropModal";
import { getCroppedImg } from "./CropModal";
import api from "core/interceptors/api";

const MediaModal = ({
    open,
    onClose,
    tabIndex,
    onTabChange,
    product,
    onAddMedia,
    onDeleteMedia,
    onZoomMedia,
    onRefresh // Add this prop to refresh products after upload
}) => {
    const [croppingQueue, setCroppingQueue] = useState([]);
    const [currentCropIndex, setCurrentCropIndex] = useState(0);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    const mobile_number = localStorage.getItem("mobile_number");

    const handleImageSelect = (event) => {
        const files = Array.from(event.target.files);
        const imageFiles = files.filter((file) => file.type.startsWith("image/"));

        if (imageFiles.length === 0) return;

        const processedFiles = imageFiles.map((file) => ({
            file,
            url: URL.createObjectURL(file),
            cropped: false,
            croppedBlob: null,
            cropState: null
        }));

        setCroppingQueue(processedFiles);
        setCurrentCropIndex(0);
        setCropModalOpen(true);

        // Reset the input
        event.target.value = "";
    };

    const handleOpenFileInput = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const handleSaveCurrentCrop = async (index, cropPixels, cropState) => {
        const updatedQueue = [...croppingQueue];
        try {
            const croppedBlob = await getCroppedImg(updatedQueue[index].url, cropPixels);
            updatedQueue[index].cropped = true;
            updatedQueue[index].croppedBlob = croppedBlob;
            updatedQueue[index].cropState = cropState;
        } catch (error) {
            console.error("Error cropping image:", error);
        }
        setCroppingQueue(updatedQueue);
    };

    const handleFinishCropping = async () => {
        const finalImages = croppingQueue
            .filter((img) => img.cropped && img.croppedBlob)
            .map((img) => img.croppedBlob);

        if (finalImages.length === 0) {
            console.log("No cropped images to upload");
            handleCropModalClose();
            return;
        }

        // Upload the cropped images
        setUploading(true);
        try {
            for (const imageBlob of finalImages) {
                const formData = new FormData();
                formData.append("media", imageBlob, "cropped_image.jpg");
                formData.append("type", "image");
                formData.append("product_id", product.product_id);
                formData.append("mobile_number", mobile_number);

                const response = await api.post("/product/upload_media", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });

                if (response.status !== 200) {
                    console.error("Failed to upload image");
                }
            }

            // Refresh the product list to show new images
            if (typeof onRefresh === "function") {
                await onRefresh();
            }

            // Close the media modal
            onClose();

            // Show success message or notification here if needed
            console.log(`Successfully uploaded ${finalImages.length} image(s)`);
        } catch (error) {
            console.error("Error uploading images:", error);
            // Show error message to user here if needed
        } finally {
            setUploading(false);
            // Clean up
            croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
            setCroppingQueue([]);
            setCropModalOpen(false);
            setCurrentCropIndex(0);
        }
    };

    const handleCropNavigation = (newIndex) => {
        if (newIndex >= 0 && newIndex < croppingQueue.length) {
            setCurrentCropIndex(newIndex);
        }
    };

    const handleCropModalClose = () => {
        croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
        setCroppingQueue([]);
        setCropModalOpen(false);
        setCurrentCropIndex(0);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Manage Media</DialogTitle>
            <DialogContent>
                <Tabs value={tabIndex} onChange={onTabChange} sx={{ mb: 2 }}>
                    <Tab label="Images" />
                    <Tab label="Videos" />
                </Tabs>

                {/* Images Tab */}
                {tabIndex === 0 && (
                    <Box p={2}>
                        <Grid container justifyContent="center">
                            <input
                                accept="image/*"
                                multiple
                                type="file"
                                ref={inputRef}
                                style={{ display: "none" }}
                                onChange={handleImageSelect}
                            />
                            <label htmlFor="upload-images">
                                <MDButton
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddPhotoAlternate />}
                                    onClick={handleOpenFileInput}
                                    disabled={uploading}
                                >
                                    {uploading ? <CircularProgress size={20} /> : "Add Image"}
                                </MDButton>
                            </label>
                        </Grid>

                        <Grid container spacing={2} mt={3}>
                            {product?.product_images?.length > 0 ? (
                                product.product_images.map((image, index) => (
                                    <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                                        <Card sx={{ borderRadius: 2, boxShadow: 2, border: "1px solid #ddd", position: "relative", overflow: "hidden" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", backgroundColor: "#f5f5f5" }}>
                                                <CardMedia
                                                    component="img"
                                                    image={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                    alt={`Product Image ${index}`}
                                                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                    onClick={() => onZoomMedia(image, "image")}
                                                />
                                            </Box>
                                            <CardActions sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <IconButton onClick={() => onZoomMedia(image, "image")} color="primary">
                                                    <ZoomIn />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => onDeleteMedia(index, "image")}
                                                    color="error"
                                                    disabled={product.product_images.length === 1 || uploading}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary" textAlign="center" width="100%" mt={2}>
                                    No images available
                                </Typography>
                            )}
                        </Grid>
                    </Box>
                )}

                {/* Videos Tab */}
                {tabIndex === 1 && (
                    <Box p={2}>
                        <Grid container justifyContent="center">
                            <MDButton
                                variant="contained"
                                color="secondary"
                                startIcon={<VideoLibrary />}
                                onClick={() => onAddMedia("video")}
                                disabled={uploading}
                            >
                                Add Video
                            </MDButton>
                        </Grid>

                        <Grid container spacing={2} mt={3}>
                            {product?.product_videos?.length > 0 ? (
                                product.product_videos.map((video, index) => (
                                    <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                                        <Card sx={{ borderRadius: 2, boxShadow: 2, border: "1px solid #ddd", position: "relative", overflow: "hidden" }}>
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px", backgroundColor: "#f5f5f5" }}>
                                                <CardMedia
                                                    component="video"
                                                    controls
                                                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                    onClick={() => onZoomMedia(video, "video")}
                                                >
                                                    <source src={video} type="video/mp4" />
                                                </CardMedia>
                                            </Box>
                                            <CardActions sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <IconButton onClick={() => onZoomMedia(video, "video")} color="primary">
                                                    <ZoomIn />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => onDeleteMedia(index, "video")}
                                                    color="error"
                                                    disabled={uploading}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary" textAlign="center" width="100%" mt={2}>
                                    No videos available
                                </Typography>
                            )}
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} color="secondary" disabled={uploading}>
                    Close
                </MDButton>
            </DialogActions>

            {/* Crop Modal */}
            <CropModal
                open={cropModalOpen}
                onClose={handleCropModalClose}
                croppingQueue={croppingQueue}
                currentIndex={currentCropIndex}
                onNavigate={handleCropNavigation}
                onSaveCurrentCrop={handleSaveCurrentCrop}
                onFinish={handleFinishCropping}
                aspectRatio={800 / 950}
                loading={uploading}
            />
        </Dialog>
    );
};

export default MediaModal;
